import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  collectVersions,
  type OnFarmImplantFreezingEmbryo,
  OnFarmImplantRequestError,
  useAssignOnFarmImplantFreezingStrawNumbers,
} from '@/services/on-farm-implant';
import { useToast } from '@/services/toast';

type AssignStrawNumbersDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  rows: OnFarmImplantFreezingEmbryo[];
};

export const AssignStrawNumbersDialog = ({
  open,
  onOpenChange,
  sessionId,
  rows,
}: AssignStrawNumbersDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [start, setStart] = useState('');
  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: assignStraws, isPending } =
    useAssignOnFarmImplantFreezingStrawNumbers();

  const matingCount = useMemo(
    () => new Set(rows.map((row) => row.matingId)).size,
    [rows],
  );
  const trimmed = start.trim();
  const invalidStart = trimmed !== '' && !/^[1-9]\d*$/.test(trimmed);
  const startNotAllowed = trimmed !== '' && matingCount > 1;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setStart('');
        setProblems([]);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (rows.length === 0 || invalidStart || startNotAllowed) return;

    setProblems([]);

    try {
      const result = await assignStraws({
        sessionId,
        input: {
          sessionEmbryoIds: rows.map((row) => row.sessionEmbryoId),
          startingStrawNumber: trimmed === '' ? null : Number(trimmed),
          versions: collectVersions(rows),
        },
      });

      handleOpenChange(false);
      toast.show({
        severity: 'success',
        summary: t('onFarmImplant.onFarmFreezing.straws.success', {
          count: result.items.length,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error('Failed to assign straw numbers:', error);
      toast.show({
        severity: 'error',
        summary: t('onFarmImplant.onFarmFreezing.straws.failed'),
      });
    }
  }, [
    assignStraws,
    handleOpenChange,
    invalidStart,
    rows,
    sessionId,
    startNotAllowed,
    t,
    toast,
    trimmed,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.onFarmFreezing.straws.title')}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.onFarmFreezing.straws.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <Label className="text-xs" htmlFor="freezing-starting-straw">
              {t('onFarmImplant.onFarmFreezing.straws.start')}
            </Label>
            <Input
              id="freezing-starting-straw"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              className="h-8 text-xs"
              placeholder={t(
                'onFarmImplant.onFarmFreezing.straws.startPlaceholder',
              )}
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </div>

          {invalidStart && (
            <p className="text-xs text-red-600">
              {t('onFarmImplant.onFarmFreezing.straws.invalidStart')}
            </p>
          )}
          {startNotAllowed && (
            <p className="text-xs text-red-600">
              {t('onFarmImplant.onFarmFreezing.straws.multipleMatings')}
            </p>
          )}

          <p className="text-sm font-medium text-gray-700">
            {t('onFarmImplant.onFarmFreezing.apply.recordCount', {
              count: rows.length,
            })}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              rows.length === 0 || invalidStart || startNotAllowed || isPending
            }
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.onFarmFreezing.straws.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
